import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Lightbulb, TrendingUp, AlertCircle, ChevronRight, Lock, Crown } from 'lucide-react';
import FoodTriggerHeatmap from './FoodTriggerHeatmap';

const LockedHeatmap = ({ onUpgrade }) => (
  <Card className="card-elevated border-0 relative overflow-hidden">
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-[#1A1514]">
        Food Trigger Heatmap
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="relative">
        <div className="space-y-3 filter blur-[6px] pointer-events-none select-none" aria-hidden="true">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-3 p-2">
              <div className="w-[100px] h-4 bg-[#E8E6E3] rounded" />
              <div className="flex-1 h-4 bg-[#A388EE]/20 rounded" style={{ width: `${70 - i * 12}%` }} />
              <div className="w-10 h-4 bg-[#E8E6E3] rounded" />
            </div>
          ))}
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 backdrop-blur-[2px] rounded-xl">
          <Lock className="w-8 h-8 text-[#FF6B4A] mb-2" />
          <p className="font-bold text-[#1A1514] text-sm">Premium Feature</p>
          <p className="text-xs text-[#8C7A76] mb-3">See which foods trigger your breakouts</p>
          <button onClick={onUpgrade} className="inline-flex items-center gap-1.5 bg-[#FF6B4A] hover:bg-[#E8552F] text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors" data-testid="heatmap-upgrade-btn">
            <Crown className="w-3.5 h-3.5" /> Unlock with Premium
          </button>
        </div>
      </div>
    </CardContent>
  </Card>
);

const PatternsSection = ({ patterns, logHistory, subscription, onUpgrade }) => {
  const isPremium = subscription === 'premium';
  return (
  <div className="space-y-6">
    {/* Top row: AI Insights + Detected Patterns */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="card-elevated border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1A1514]">
            <Lightbulb className="w-5 h-5 text-[#4ADE80]" />
            AI Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gradient-to-br from-[#4ADE80]/10 to-[#FF6B4A]/5 rounded-xl p-6">
            <div className="text-[#1A1514] text-sm leading-relaxed" data-testid="ai-insights">
              <ReactMarkdown
                components={{
                  h1: ({ node, ...p }) => <h4 className="font-heading font-bold text-base text-[#1A1514] mt-4 first:mt-0 mb-2" {...p} />,
                  h2: ({ node, ...p }) => <h4 className="font-heading font-bold text-base text-[#1A1514] mt-4 first:mt-0 mb-2" {...p} />,
                  h3: ({ node, ...p }) => <h4 className="font-heading font-bold text-base text-[#1A1514] mt-4 first:mt-0 mb-2" {...p} />,
                  h4: ({ node, ...p }) => <h5 className="font-heading font-bold text-sm text-[#1A1514] mt-3 first:mt-0 mb-1.5" {...p} />,
                  p: ({ node, ...p }) => <p className="mb-2 last:mb-0 leading-relaxed" {...p} />,
                  ul: ({ node, ...p }) => <ul className="space-y-2 mb-3 last:mb-0" {...p} />,
                  ol: ({ node, ...p }) => <ol className="space-y-2 mb-3 last:mb-0 list-decimal pl-4" {...p} />,
                  li: ({ node, ...p }) => (
                    <li className="flex gap-2">
                      <span className="text-[#FF6B4A] font-bold flex-shrink-0 mt-0.5">•</span>
                      <span className="flex-1">{p.children}</span>
                    </li>
                  ),
                  strong: ({ node, ...p }) => <strong className="font-bold text-[#E8552F]" {...p} />,
                }}
              >
                {patterns?.ai_insights || 'Start logging to get personalized AI insights about your skin patterns.'}
              </ReactMarkdown>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="card-elevated border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1A1514]">
            <TrendingUp className="w-5 h-5 text-[#A388EE]" />
            Detected Patterns
          </CardTitle>
        </CardHeader>
        <CardContent>
          {patterns?.patterns?.length > 0 ? (
            <div className="space-y-4">
              {patterns.patterns.map((pattern) => (
                <div key={pattern.type} className={`pattern-insight ${pattern.severity === 'high' ? 'bg-[#A388EE]' : 'bg-[#FF6B4A]'}`}>
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">{pattern.title}</p>
                      <p className="text-sm opacity-90 mt-1">{pattern.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#8C7A76] text-center py-8">Keep logging daily to discover patterns in your skin health.</p>
          )}
        </CardContent>
      </Card>
    </div>

    {/* Food Trigger Heatmap — Premium only */}
    {isPremium ? <FoodTriggerHeatmap /> : <LockedHeatmap onUpgrade={onUpgrade} />}

    {/* Log History */}
    <Card className="card-elevated border-0">
      <CardHeader>
        <CardTitle className="text-[#1A1514]">Recent Logs</CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[250px]">
          <div className="space-y-3">
            {logHistory.map((log) => (
              <div key={log.log_id || log.date} className="flex items-center justify-between p-4 bg-[#FFF2EC] rounded-xl">
                <div>
                  <p className="font-medium text-[#1A1514]">{log.date}</p>
                  <p className="text-sm text-[#8C7A76]">
                    Acne: {log.acne_severity}/10 &bull; Bloating: {log.bloating_level}/10 &bull; Water: {log.water_glasses}
                    {log.food_intake?.length > 0 && <> &bull; {log.food_intake.join(', ')}</>}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-[#8C7A76]" />
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  </div>
  );
};

export default PatternsSection;
